import React, { useEffect } from "react";
import { Link, useNavigate } from "react-router-dom";
import {
  Package,
  Brain,
  Shield,
  Clock,
  RotateCcw,
  Plus,
  ArrowRight,
  FileText,
  BarChart3,
  BookOpen,
  Settings,
  Layers,
  Camera,
  HelpCircle,
} from "lucide-react";
import { useTranslation } from "react-i18next";
import { useAuth } from "@/contexts/AuthContext";
import { useIsMobile } from "@/hooks/useMediaQuery";
import Footer from "@/components/Footer";

const HomePage: React.FC = () => {
  const { t } = useTranslation();
  const { isAdmin } = useAuth();
  const isMobile = useIsMobile();
  const navigate = useNavigate();

  // Redirect mobile users to sessions page
  useEffect(() => {
    if (isMobile) {
      navigate('/sessions', { replace: true });
    }
  }, [isMobile, navigate]);

  const features = [
    {
      icon: Package,
      title: "Smart Inventory",
      description:
        "Seamless AI-driven cataloging of luxury items with precision and consistency",
      iconBgColor: "bg-blue-100",
      iconColor: "text-blue-600",
    },
    {
      icon: Brain,
      title: "Advanced Recognition",
      description:
        "Advanced algorithms ensure accurate recognition and model matching across collections",
      iconBgColor: "bg-purple-100",
      iconColor: "text-purple-600",
    },
    {
      icon: Shield,
      title: "Trusted Security",
      description:
        "All boutique data and images are processed with full confidentiality and safeguarded storage options",
      iconBgColor: "bg-green-100",
      iconColor: "text-green-600",
    },
    {
      icon: Clock,
      title: "Instant Insights",
      description:
        "Receive real-time analysis and inventory updates with confidence scoring",
      iconBgColor: "bg-orange-100",
      iconColor: "text-orange-600",
    },
    {
      icon: RotateCcw,
      title: "Remote Re-Analysis",
      description:
        "Captured images can be revisited, re-processed, and updated remotely for continuous inventory accuracy",
      iconBgColor: "bg-indigo-100",
      iconColor: "text-indigo-600",
    },
    {
      icon: Plus,
      title: "On-the-Fly Expansion",
      description:
        "Easily add new collections or models with just a few exemplar images — no retraining required, fully scalable",
      iconBgColor: "bg-emerald-100",
      iconColor: "text-emerald-600",
    },
  ];

  const navigationSections = [
    {
      title: "Analysis Reports",
      description: "View detailed analysis reports and session history. Track your inventory analysis progress and export results.",
      link: "/reports",
      icon: BarChart3,
      adminOnly: false,
      alignment: "left",
    },
    {
      title: "Product Catalog",
      description: "Manage your product database and classification models. Add new items and update existing collections.",
      link: "/catalog",
      icon: Layers,
      adminOnly: true,
      alignment: "right",
    },
    {
      title: "Guidelines & Best Practices",
      description: "Learn how to get the best results from your inventory analysis. Tips for optimal image capture and processing.",
      link: "/guidelines",
      icon: BookOpen,
      adminOnly: false,
      alignment: "left",
    },
    {
      title: "User Manual",
      description: "Comprehensive documentation and step-by-step instructions for using all platform features effectively.",
      link: "/manual",
      icon: FileText,
      adminOnly: false,
      alignment: "right",
    },
    {
      title: "Admin Panel",
      description: "Manage users, rooms, and system settings. Monitor platform usage and configure organizational preferences.",
      link: "/admin",
      icon: Settings,
      adminOnly: true,
      alignment: "left",
    },
  ].filter(section => !section.adminOnly || isAdmin);

  return (
    <div className="space-y-12 pb-12 pt-8">
      {/* Hero Section */}
      <div className="text-center space-y-8">
        <div className="space-y-6">
          <h1 className="text-3xl font-serif font-bold text-gray-900 sm:text-4xl md:text-5xl">
            <span className="text-orange-600">L</span>uxury <span className="text-orange-600">U</span>nified <span className="text-orange-600">M</span>anagement <span className="text-orange-600">I</span>nventory <span className="text-orange-600">S</span>ystem
          </h1>
          <p className="mx-auto max-w-4xl text-3xl text-gray-800">
            {t("home.hero_subtitle")}
          </p>
          <p className="mx-auto max-w-3xl text-xl text-gray-600">
            Upload images of luxury items and let <span className="text-orange-600 font-semibold">LUMIS</span> deliver instant detection, classification, and inventory integration.
          </p>
          <p className="mx-auto max-w-3xl text-xl text-gray-600">
            Powered by tailored vision models and refined classification algorithms, <span className="text-orange-600 font-semibold">LUMIS</span> brings boutique-grade precision and reliability to your asset management.
          </p>
        </div>

        <div className="flex flex-col sm:flex-row gap-4 justify-center items-center">
          <Link to="/sessions" className="btn-primary px-8 py-3 text-lg">
            {t("home.get_started_cta")}
            <ArrowRight className="ml-2 h-5 w-5" />
          </Link>

          <Link
            to="/manual"
            className="group relative overflow-hidden inline-flex items-center py-3 text-base font-medium text-gray-700 bg-gray-100 hover:bg-gray-200 border border-gray-300 rounded-lg transition-all duration-300 hover:px-6 px-3"
          >
            <HelpCircle className="h-4 w-4 text-blue-600 flex-shrink-0" />
            <span className="ml-0 whitespace-nowrap opacity-0 max-w-0 group-hover:opacity-100 group-hover:max-w-xs group-hover:ml-2 transition-all duration-300 ease-out overflow-hidden">
              Where to start?
            </span>
          </Link>

          <Link
            to="/guidelines"
            className="group relative overflow-hidden inline-flex items-center py-3 text-base font-medium text-gray-700 bg-gray-100 hover:bg-gray-200 border border-gray-300 rounded-lg transition-all duration-300 hover:px-6 px-3"
          >
            <Camera className="h-4 w-4 text-green-600 flex-shrink-0" />
            <span className="ml-0 whitespace-nowrap opacity-0 max-w-0 group-hover:opacity-100 group-hover:max-w-xs group-hover:ml-2 transition-all duration-300 ease-out overflow-hidden">
              How to take photos?
            </span>
          </Link>
        </div>
      </div>

      {/* Features Grid - 6 boxes in 2 rows, 3 columns */}
      <div className="grid grid-cols-1 gap-6 md:grid-cols-2 lg:grid-cols-3">
        {features.map((feature, index) => {
          const Icon = feature.icon;
          return (
            <div
              key={index}
              className="card p-6 text-center space-y-4 hover:shadow-md transition-shadow"
            >
              <div className={`mx-auto h-12 w-12 ${feature.iconBgColor} rounded-lg flex items-center justify-center`}>
                <Icon className={`h-6 w-6 ${feature.iconColor}`} />
              </div>
              <h3 className="text-lg font-semibold text-gray-900">
                {feature.title}
              </h3>
              <p className="text-gray-600">{feature.description}</p>
            </div>
          );
        })}
      </div>

      {/* How it Works */}
      <div className="bg-white rounded-xl p-6 shadow-sm">
        <h2 className="text-3xl font-serif font-bold text-center text-gray-900 mb-6">
          {t("home.how_it_works_title")}
        </h2>

        <div className="grid grid-cols-1 md:grid-cols-4 gap-6">
          <div className="text-center space-y-4">
            <div className="mx-auto w-16 h-16 bg-primary-100 rounded-full flex items-center justify-center text-2xl font-bold text-primary-600">
              1
            </div>
            <h3 className="text-lg font-semibold text-gray-900">
              {t("home.step1_title")}
            </h3>
            <p className="text-gray-600">{t("home.step1_desc")}</p>
          </div>

          <div className="text-center space-y-4">
            <div className="mx-auto w-16 h-16 bg-primary-100 rounded-full flex items-center justify-center text-2xl font-bold text-primary-600">
              2
            </div>
            <h3 className="text-lg font-semibold text-gray-900">
              {t("home.step2_title")}
            </h3>
            <p className="text-gray-600">{t("home.step2_desc")}</p>
          </div>

          <div className="text-center space-y-4">
            <div className="mx-auto w-16 h-16 bg-primary-100 rounded-full flex items-center justify-center text-2xl font-bold text-primary-600">
              3
            </div>
            <h3 className="text-lg font-semibold text-gray-900">
              {t("home.step3_title")}
            </h3>
            <p className="text-gray-600">{t("home.step3_desc")}</p>
          </div>

          <div className="text-center space-y-4">
            <div className="mx-auto w-16 h-16 bg-primary-100 rounded-full flex items-center justify-center text-2xl font-bold text-primary-600">
              4
            </div>
            <h3 className="text-lg font-semibold text-gray-900">
              {t("home.step4_title")}
            </h3>
            <p className="text-gray-600">{t("home.step4_desc")}</p>
          </div>
        </div>
      </div>

      {/* Navigation Sections */}
      <div className="space-y-6">
        <h2 className="text-3xl font-serif font-bold text-center text-gray-900 mb-8">
          Explore Our Platform
        </h2>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-6 max-w-6xl mx-auto">
          {navigationSections.map((section, index) => {
            const Icon = section.icon;

            return (
              <Link
                key={index}
                to={section.link}
                className="group block"
              >
                <div className="bg-white rounded-xl p-6 shadow-sm border hover:shadow-lg transition-all duration-300 transform hover:-translate-y-1 h-full">
                  <div className="space-y-4">
                    <div className="flex items-center gap-4 justify-center md:justify-start">
                      <div className="h-12 w-12 bg-primary-100 rounded-lg flex items-center justify-center group-hover:bg-primary-200 transition-colors">
                        <Icon className="h-6 w-6 text-primary-600" />
                      </div>
                      <h3 className="text-xl font-serif font-bold text-gray-900">
                        {section.title}
                      </h3>
                      <ArrowRight className="h-6 w-6 text-primary-600 opacity-0 group-hover:opacity-100 transform translate-x-0 group-hover:translate-x-2 transition-all duration-300 ml-auto" />
                    </div>
                    <p className="text-gray-600 text-center md:text-left">
                      {section.description}
                    </p>
                  </div>
                </div>
              </Link>
            );
          })}
        </div>
      </div>

      {/* Footer */}
      <Footer />
    </div>
  );
};

export default HomePage;
